import { useEffect, useState } from 'react';

/*
 * Sizing the tracks to the page.
 *
 * A Highcharts chart needs a pixel height -- there is no `height: 100%` for it
 * -- so "fill whatever is left" has to be computed. The alternative is turning
 * the whole chain from the page down to the plot row into a flex layout and
 * observing the leftover box, which means threading `min-height: 0` through the
 * Card, the Tabs root, TabPanels and TabPanel via page-level overrides of
 * design-system internals. Same pixels, five more places to break.
 *
 * Nothing here is a hardcoded chrome allowance. The height is derived from two
 * measurements, both taken from the DOM:
 *
 *   available = (bottom of the scroller's content box)
 *             - (top of the track scroller)
 *             - (everything that sits below the track scroller)
 *
 * so the page header, a warning banner, the tab strip
 * and the card's own padding are all accounted for by measuring them instead of
 * guessing at them. The track headers are deducted separately, since they live
 * inside the scroller and stay pinned while the plots scroll under them.
 */

/**
 * Below this the tracks stop being readable and it is better to let the page
 * scroll than to keep shrinking them.
 */
export const MIN_TRACK_HEIGHT = 360;

export interface FillHeights {
  /** Height for the scroller: the pinned header row plus the plots. */
  viewport: number;
  /** Height for one track's chart box. */
  plot: number;
}

export interface FillMetrics {
  /** Viewport-relative bottom of the page scroller's content box. */
  contentBottom: number;
  /** Viewport-relative top of the track scroller. */
  viewportTop: number;
  /**
   * How far the page is scrolled. Added back to `viewportTop` so a measurement
   * taken while scrolled produces the same height as one taken at the top --
   * without it, scrolling down would grow the tracks, which grows the scroll
   * range that caused it.
   */
  scrollTop: number;
  /** Padding, borders and siblings between the track scroller and the content box. */
  chromeBelow: number;
  /**
   * Everything inside the scroller that is not plot: the header row, which stays
   * pinned while the plots scroll under it, plus the frame each track cell draws
   * around its chart.
   */
  chromeInside: number;
}

/** The chart height that makes the tracks end where the page does. */
export const availableTrackHeight = (metrics: FillMetrics): number =>
  Math.max(
    MIN_TRACK_HEIGHT,
    Math.round(
      metrics.contentBottom -
        (metrics.viewportTop + metrics.scrollTop) -
        metrics.chromeBelow -
        metrics.chromeInside
    )
  );

const scrollParentOf = (node: HTMLElement): HTMLElement | null => {
  let current = node.parentElement;
  while (current) {
    const overflowY = window.getComputedStyle(current).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
};

const pixels = (value: string): number => Number.parseFloat(value) || 0;

/**
 * Everything below `node` on the way up to `stop`, exclusive.
 *
 * Each step contributes the gap between an ancestor's bottom edge and its
 * child's -- that ancestor's bottom padding and border, plus any sibling after
 * the child. None of it depends on the plot height, which is what makes this
 * safe to feed back into that height.
 *
 * `stop` itself is excluded on purpose: it is the scroll container, so when the
 * content is shorter than the viewport its bottom edge is the bottom of the
 * empty space rather than the bottom of the content -- exactly the gap this is
 * measuring away. Its padding is subtracted from the content box instead.
 */
const measureChromeBelow = (
  node: HTMLElement,
  stop: HTMLElement | null
): number => {
  let total = 0;
  let child: HTMLElement = node;
  let parent = child.parentElement;
  while (parent && parent !== stop) {
    total +=
      parent.getBoundingClientRect().bottom -
      child.getBoundingClientRect().bottom;
    child = parent;
    parent = parent.parentElement;
  }
  return total;
};

/**
 * The heights that make the tracks end where the page does.
 *
 * Measures the **scroller**, not the plot row, and deducts the header row to get
 * the per-track height. Two reasons, both from the depth-scale modes:
 *
 * - At a fixed scale the plots are taller than the page and the scroller has to
 *   clip them, which means it needs a height of its own. Its top edge does not
 *   move when its contents grow, so measuring there is stable; the plot row's
 *   top edge would sit inside a box whose scroll offset it follows.
 * - Once the scroller scrolls vertically it becomes the nearest scrolling
 *   ancestor of the plot row, so a measurement anchored on the row would resolve
 *   against the very box this is sizing.
 *
 * `headerRef` is both deducted and observed -- the track headers are the one
 * thing above the plots whose size changes without a window resize, and the
 * amount to deduct is exactly their height. `frame` is the rest of the chrome
 * inside the scroller, which the caller knows and this cannot see: the borders a
 * track cell draws around its chart. Two pixels of it are enough to put the
 * content past the scroller and raise a scrollbar the layout does not need.
 *
 * Falls back to `fallback` and stays there whenever the scroller measures zero
 * width, which is what a `keepMounted` tab panel does while it is
 * `display: none`. Re-measuring a hidden chart down to the floor would bring it
 * back at the wrong size.
 */
export const useFillHeights = (
  viewportRef: React.RefObject<HTMLElement>,
  headerRef: React.RefObject<HTMLElement>,
  fallback: number,
  frame = 0
): FillHeights => {
  const [heights, setHeights] = useState<FillHeights>({
    viewport: fallback,
    plot: fallback
  });

  useEffect(() => {
    const measure = () => {
      const node = viewportRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (rect.width === 0) return;

      // Deliberately not `node.parentElement`: the search must start above the
      // scroller, which scrolls vertically itself once a fixed scale is picked.
      const scroller = scrollParentOf(node);
      const chromeInside = (headerRef.current?.offsetHeight ?? 0) + frame;

      const style = scroller ? window.getComputedStyle(scroller) : null;
      const plot = availableTrackHeight({
        contentBottom:
          scroller && style
            ? scroller.getBoundingClientRect().bottom -
              pixels(style.paddingBottom) -
              pixels(style.borderBottomWidth)
            : window.innerHeight,
        viewportTop: rect.top,
        scrollTop: scroller ? scroller.scrollTop : 0,
        chromeBelow: measureChromeBelow(node, scroller),
        chromeInside
      });

      // Kept consistent by construction: the scroller is exactly the header
      // plus one framed track, so in 'full' scale the plots fit it without
      // scrolling.
      const viewport = plot + chromeInside;
      setHeights(current =>
        current.plot === plot && current.viewport === viewport
          ? current
          : { plot, viewport }
      );
    };

    measure();
    window.addEventListener('resize', measure);

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer && headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [viewportRef, headerRef, frame]);

  return heights;
};
